import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Bell, X, Check, CheckCheck, Truck, Receipt, AlertCircle, Volume2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { isWelcomeSequenceDone, onWelcomeSequenceDone, playNotificationAlert } from '../utils/welcomeTones';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Soothing notification chime (soft bell, respects user volume setting)
const playLoudNotificationSound = () => {
  try { playNotificationAlert(); } catch (e) { console.debug('Notification sound unavailable'); }
};

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastUnreadCount, setLastUnreadCount] = useState(0);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const dropdownRef = useRef(null);
  const firstFetchRef = useRef(true);

  const getAxiosConfig = () => ({
    withCredentials: true
  });

  // Auto-request notification permission on component mount - AGGRESSIVE
  useEffect(() => {
    const requestNotificationPermission = async () => {
      if ('Notification' in window) {
        if (Notification.permission === 'granted') {
          setPermissionGranted(true);
        } else if (Notification.permission === 'default') {
          // Show a brief info message before requesting
          toast.info('Enable notifications to stay updated on new deliveries!', { duration: 2000 });
          
          // Small delay to let user see the message
          setTimeout(async () => {
            try {
              const permission = await Notification.requestPermission();
              if (permission === 'granted') {
                setPermissionGranted(true);
                toast.success('🔔 Notifications enabled! You will hear a gentle chime for new updates.');
                // Confirmation sound waits until welcome tone + greeting finish
                onWelcomeSequenceDone(() => setTimeout(() => playLoudNotificationSound(), 500));
              } else if (permission === 'denied') {
                toast.error('Notifications blocked. Enable in browser settings to receive alerts.');
              }
            } catch (e) {
              console.debug('Permission request failed:', e);
            }
          }, 2500);
        } else if (Notification.permission === 'denied') {
          // User previously denied - show instructions once per session
          if (!sessionStorage.getItem('notif_blocked_warned')) {
            sessionStorage.setItem('notif_blocked_warned', '1');
            toast.warning('Notifications are blocked. Click the lock icon in browser address bar to enable.', { duration: 5000 });
          }
        }
      }
    };
    
    // Request permission immediately on mount
    requestNotificationPermission();
    
    // Unlock audio context on first user interaction (required for mobile)
    const unlockAudio = () => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContext.resume().then(() => {
          console.debug('Audio context unlocked');
        });
      } catch (e) {}
    };
    
    // Add listeners for user interaction to unlock audio
    document.addEventListener('click', unlockAudio, { once: true });
    document.addEventListener('touchstart', unlockAudio, { once: true });
    document.addEventListener('keydown', unlockAudio, { once: true });
    
    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API}/notifications`, getAxiosConfig()),
        axios.get(`${API}/notifications/unread-count`, getAxiosConfig())
      ]);
      setNotifications(notifRes.data);
      const newUnreadCount = countRes.data.count;
      
      // Play loud sound ONLY for NEW notifications (not on mount / page change)
      // and NEVER before the welcome tone + voice greeting sequence has finished
      if (!firstFetchRef.current && newUnreadCount > lastUnreadCount && isWelcomeSequenceDone()) {
        playLoudNotificationSound();
        
        // Also show browser notification
        if (permissionGranted && notifRes.data.length > 0) {
          const latestNotif = notifRes.data.find(n => !n.is_read);
          if (latestNotif) {
            new Notification('JMEPL Alert!', {
              body: latestNotif.message,
              icon: '/favicon.ico',
              requireInteraction: true,
              tag: 'jmepl-notification'
            });
          }
        }
      }
      
      firstFetchRef.current = false;
      setLastUnreadCount(newUnreadCount);
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [lastUnreadCount, permissionGranted]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, getAxiosConfig());
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, getAxiosConfig());
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  // Test sound button - plays loud alert
  const testNotificationSound = () => {
    playLoudNotificationSound();
    toast.success('🔊 Sound test played! This is how new notifications will sound.');
  };

  // Poll for new notifications every 15 seconds (faster for real-time feel)
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_requirement':
        return <Truck className="w-4 h-4 text-blue-500" />;
      case 'truck_assigned':
        return <Truck className="w-4 h-4 text-green-500" />;
      case 'voucher_update':
        return <Receipt className="w-4 h-4 text-purple-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className={`w-5 h-5 ${unreadCount > 0 ? 'text-red-600 animate-bounce' : 'text-slate-600'}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-bold animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div data-testid="notification-panel" className="fixed sm:absolute right-2 sm:right-0 top-14 sm:top-auto sm:mt-2 w-[calc(100vw-1rem)] sm:w-80 max-w-[320px] bg-white rounded-lg shadow-xl border border-slate-200 z-50 overflow-hidden flex flex-col max-h-[calc(100vh-4.5rem)] sm:max-h-[80vh]">
          {/* Header */}
          <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between shrink-0">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Notifications
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={testNotificationSound}
                className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                title="Test notification sound"
              >
                <Volume2 className="w-3 h-3" />
              </button>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <CheckCheck className="w-3 h-3" />
                  Mark all read
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
            {loading ? (
              <div className="px-4 py-8 text-center text-slate-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-slate-500">
                <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-4 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${
                    !notification.is_read ? 'bg-blue-50/50' : ''
                  }`}
                  onClick={() => !notification.is_read && markAsRead(notification.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notification.is_read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                        {notification.title}
                      </p>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                    {!notification.is_read && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-center shrink-0">
              <span className="text-xs text-slate-500">
                Showing {notifications.length} notifications
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
